import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { FiArrowLeft, FiCheckCircle } from 'react-icons/fi';

const ForgotPasswordForm = ({ switchToLogin, setAuthError, setAuthMessage }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => setCooldown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;

    setLoading(true);
    setAuthError('');
    setAuthMessage('');

    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: redirectUrl,
      });

      if (error) {
        const msg = error.message || '';
        if (
          msg.toLowerCase().includes('rate limit') ||
          msg.toLowerCase().includes('too many') ||
          msg.toLowerCase().includes('over_email_send_rate_limit')
        ) {
          throw new Error('Too many requests. Please wait a minute before requesting another reset email.');
        }
        throw error;
      }

      setEmailSent(true);
      setCooldown(60);
      setAuthMessage('Password reset link sent! Check your inbox.');
    } catch (err) {
      setAuthError(err.message || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (cooldown === 0 && !loading) {
      handleSubmit();
    }
  };

  if (emailSent) {
    return (
      <div className="auth-form" style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#ecfdf5',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '1.75rem',
          }}
        >
          <FiCheckCircle />
        </div>

        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1e293b', marginBottom: '0.5rem' }}>
          Check your inbox
        </h3>

        <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.25rem', lineHeight: '1.5' }}>
          We sent a password reset link to <strong style={{ color: '#0f172a' }}>{email}</strong>. Click the link in
          the email to set a new password.
        </p>

        <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '1.5rem', lineHeight: '1.4' }}>
          Did not receive the email? Check your spam folder or request another link below.
        </p>

        <button
          type="button"
          onClick={handleResend}
          className="submit-button"
          disabled={loading || cooldown > 0}
          style={{ marginBottom: '0.75rem' }}
        >
          {loading ? (
            <>
              <span className="loading-spinner">↻</span> Sending...
            </>
          ) : cooldown > 0 ? (
            `Resend link in ${cooldown}s`
          ) : (
            'Resend reset email'
          )}
        </button>

        <button
          type="button"
          onClick={() => switchToLogin()}
          className="toggle-button"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
        >
          <FiArrowLeft size={14} /> Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem', textAlign: 'center' }}>
        Enter your account email address and we will send you a link to reset your password.
      </p>

      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <div style={{ position: 'relative' }}>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setAuthError('');
              setAuthMessage('');
              setEmail(e.target.value);
            }}
            className="form-input"
            placeholder="email@example.com"
            required
            disabled={loading}
            autoComplete="email"
          />
        </div>
      </div>

      <button type="submit" className="submit-button" disabled={loading || !email.trim()}>
        {loading ? (
          <>
            <span className="loading-spinner">↻</span> Sending Link...
          </>
        ) : (
          'Send Reset Link'
        )}
      </button>

      <button
        type="button"
        onClick={() => switchToLogin()}
        className="toggle-button"
        disabled={loading}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
      >
        <FiArrowLeft size={14} /> Back to Sign In
      </button>
    </form>
  );
};

export default ForgotPasswordForm;

